<script setup lang="ts">
defineOptions({
    inheritAttrs: false,
});

interface Props {
    src: string;
    alt: HTMLImageElement['alt'];
    width?: number;
    height?: number;
    fetchpriority?: HTMLImageElement['fetchPriority'];
    loading?: HTMLImageElement['loading'];
    sizes?: string;
    fallbackSrc?: string | null;
    loadTimeoutMs?: number;
}

const props = withDefaults(defineProps<Props>(), {
    loading: "lazy",
    fallbackSrc: "/not_found.png",
    loadTimeoutMs: 15_000,
});

const attrs = useAttrs();
const appConfig = useAppConfig();
const isS3 = computed(() => appConfig.provider === "s3");
type ImageStatus =
    | "loading"
    | "loaded"
    | "fallback-loading"
    | "fallback-loaded"
    | "failed";

const status = ref<ImageStatus>("loading");
const imageRef = ref<HTMLImageElement | null>(null);
let loadTimeoutId: ReturnType<typeof setTimeout> | undefined;

const wrapperClass = computed(() => attrs.class);
const wrapperStyle = computed(() => attrs.style);
const imageAttrs = computed(() => {
    const { onError: _onError, ...rest } = attrs;

    return rest;
});
const isUsingFallback = computed(
    () =>
        status.value === "fallback-loading" ||
        status.value === "fallback-loaded",
);
const isFailed = computed(() => status.value === "failed");
const isLoaded = computed(
    () =>
        status.value === "loaded" ||
        status.value === "fallback-loading" ||
        status.value === "fallback-loaded" ||
        isFailed.value,
);

async function syncLoadedState() {
    await nextTick();

    const image = imageRef.value;
    if (!image) return;

    if (!image.complete) {
        startLoadTimeout();
        return;
    }

    if (image.naturalWidth > 0) {
        onLoad();
        return;
    }

    // A cached failed request may finish before Vue attaches @error.
    await onError();
}

function clearLoadTimeout() {
    if (!loadTimeoutId) return;

    clearTimeout(loadTimeoutId);
    loadTimeoutId = undefined;
}

function startLoadTimeout() {
    clearLoadTimeout();

    if (
        !import.meta.client ||
        props.loadTimeoutMs <= 0 ||
        (props.loading !== "eager" && !isUsingFallback.value)
    ) {
        return;
    }

    loadTimeoutId = setTimeout(() => {
        void onError();
    }, props.loadTimeoutMs);
}

function onLoad() {
    clearLoadTimeout();
    status.value = isUsingFallback.value ? "fallback-loaded" : "loaded";
}

async function onError() {
    clearLoadTimeout();

    if (
        !isUsingFallback.value &&
        props.fallbackSrc &&
        props.src !== props.fallbackSrc
    ) {
        status.value = "fallback-loading";
        await syncLoadedState();
        return;
    }

    // Unmount the failed image so the browser cannot retry the same URL.
    status.value = "failed";
}

watch(
    [() => props.src, () => props.fallbackSrc],
    async () => {
        status.value = "loading";
        await syncLoadedState();
    },
    { immediate: true },
);

onMounted(() => {
    syncLoadedState();
});

onBeforeUnmount(() => {
    clearLoadTimeout();
});
</script>

<template>
    <NuxtImg
        v-if="isS3 && !isUsingFallback && !isFailed"
        custom
        :src="src"
        :alt="alt"
        :width="width"
        :height="height"
        :loading="loading"
        provider="customS3"
        :fetchpriority="fetchpriority"
        :sizes="sizes"
        v-slot="{ src: imageSrc, imgAttrs }"
    >
        <div
            class="ui-image-shell"
            :class="[wrapperClass, { 'is-loaded': isLoaded }]"
            :style="wrapperStyle"
        >
            <img
                ref="imageRef"
                v-bind="{ ...imgAttrs, ...imageAttrs }"
                :src="imageSrc"
                :alt="alt"
                @load="onLoad"
                @error="onError"
            />
        </div>
    </NuxtImg>
    <div
        v-else
        class="ui-image-shell"
        :class="[
            wrapperClass,
            {
                'is-loaded': isLoaded,
                'is-placeholder': isUsingFallback || isFailed,
                'is-failed': isFailed,
            },
        ]"
        :style="wrapperStyle"
        :role="isFailed ? 'img' : undefined"
        :aria-label="isFailed ? alt : undefined"
    >
        <img
            v-if="!isFailed"
            ref="imageRef"
            v-bind="imageAttrs"
            :src="isUsingFallback ? (fallbackSrc ?? src) : src"
            :alt="alt"
            :width="width"
            :height="height"
            :loading="isUsingFallback ? 'eager' : loading"
            :sizes="isUsingFallback ? undefined : sizes"
            @load="onLoad"
            @error="onError"
        />
    </div>
</template>

<style scoped>
.ui-image-shell.is-placeholder,
.ui-image-shell.is-failed {
    background: #f3f3f3;
}
</style>
